import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Card, 
  CardContent,
  CardActions,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Download, 
  Description, 
  GetApp,
  Close as CloseIcon,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';
import { DownloadableFile } from '../../types/documentation';
import { generateVideoSDKDemoHTML } from '../../templates/videoSDKDemoTemplate';
import LinkedText from '../common/LinkedText';

interface DownloadSourceFilesSectionProps {
  downloadSourceFiles: DownloadableFile[];
}

const DownloadSourceFilesSection: React.FC<DownloadSourceFilesSectionProps> = ({
  downloadSourceFiles
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [appId, setAppId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [currentFile, setCurrentFile] = useState<DownloadableFile | null>(null);
  const [errors, setErrors] = useState({ appId: '', secretKey: '' });

  const validateInputs = () => {
    const newErrors = { appId: '', secretKey: '' };
    let isValid = true;

    if (!appId.trim()) {
      newErrors.appId = 'App ID is required';
      isValid = false;
    } else if (appId.trim().length < 10) {
      newErrors.appId = 'App ID should be at least 10 characters';
      isValid = false;
    }

    if (!secretKey.trim()) {
      newErrors.secretKey = 'Secret Key is required';
      isValid = false;
    } else if (secretKey.trim().length < 20) {
      newErrors.secretKey = 'Secret Key should be at least 20 characters';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleDownload = (file: DownloadableFile) => {
    setCurrentFile(file);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setAppId('');
    setSecretKey('');
    setErrors({ appId: '', secretKey: '' });
    setCurrentFile(null);
    setShowSecretKey(false);
  };

  const handleDownloadWithCredentials = () => {
    if (!validateInputs()) {
      return;
    }

    // Generate the customized HTML using the imported template
    const customizedHTML = generateVideoSDKDemoHTML(appId.trim(), secretKey.trim());
    
    // Create blob and download
    const blob = new Blob([customizedHTML], { type: 'text/html' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sampark-video-sdk-demo-${Date.now()}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    handleClose();
  };

  if (!downloadSourceFiles || downloadSourceFiles.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 4, textAlign: 'left' }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: 3,
        pb: 2,
        borderBottom: '2px solid #e3f2fd'
      }}>
        <GetApp sx={{ mr: 1, color: '#1976d2', fontSize: 28 }} />
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600, 
            color: '#1976d2',
            textAlign: 'left'
          }}
        >
          Download Source Files
        </Typography>
      </Box>
      
      <Typography 
        variant="body1" 
        paragraph 
        color="text.secondary" 
        sx={{ 
          mb: 3,
          textAlign: 'left',
          fontSize: '1rem',
          lineHeight: 1.6
        }}
      >
        Access complete source code examples and demo applications to jumpstart your integration.
      </Typography>

      <Box sx={{ 
        display: 'grid', 
        gap: 3,
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(auto-fit, minmax(400px, 1fr))'
        }
      }}>
        {downloadSourceFiles.map((file, index) => (
          <Card 
            key={index} 
            elevation={3}
            sx={{ 
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
              }
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'flex-start', 
                mb: 2 
              }}>
                <Description 
                  sx={{ 
                    mr: 2, 
                    mt: 0.5,
                    color: '#1976d2',
                    fontSize: 24 
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 600,
                      color: '#1976d2',
                      textAlign: 'left',
                      mb: 1
                    }}
                  >
                    {file.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      textAlign: 'left',
                      lineHeight: 1.5
                    }}
                  >
                    <LinkedText text={file.description} />
                  </Typography>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography 
                variant="body2" 
                sx={{ 
                  textAlign: 'left',
                  color: 'text.primary',
                  lineHeight: 1.6,
                  fontSize: '0.9rem'
                }}
              >
                <LinkedText text={file.details} />
              </Typography>

              {file.code && (
                <Paper 
                  elevation={1}
                  sx={{ 
                    mt: 2,
                    p: 2,
                    bgcolor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: 1
                  }}
                >
                  <Box 
                    component="pre" 
                    sx={{ 
                      m: 0, 
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '0.8rem',
                      lineHeight: 1.4,
                      overflow: 'auto',
                      color: '#212529',
                      textAlign: 'left',
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {file.code}
                  </Box>
                </Paper>
              )}
            </CardContent>
            
            <CardActions sx={{ p: 3, pt: 0, justifyContent: 'flex-start' }}>
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => handleDownload(file)}
                sx={{
                  backgroundColor: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#1565c0'
                  },
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: '0.9rem'
                }}
              >
                Download Files
              </Button>
            </CardActions>
          </Card>
        ))}
      </Box>
      
      <Paper 
        elevation={1}
        sx={{ 
          mt: 3,
          p: 3,
          backgroundColor: '#fff3e0',
          border: '1px solid #ffcc02',
          borderRadius: 2
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'left',
            color: '#ef6c00',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Description sx={{ mr: 1, fontSize: 20 }} />
          Note: Make sure to replace placeholder credentials with your actual App ID and Secret Key from the Admin Dashboard.
        </Typography>
      </Paper>
      
      {/* Credentials Input Dialog */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1976d2' }}>
            Enter Your Credentials
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            Enter your App ID and Secret Key from the Sampark Admin Dashboard. 
            These credentials will be automatically inserted into the demo HTML file.
          </Alert>
          
          <TextField
            fullWidth
            label="App ID"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
            error={!!errors.appId}
            helperText={errors.appId || 'Your application ID from the admin dashboard'}
            margin="normal"
            variant="outlined"
            placeholder="e.g., JqzeNHU10Oi9CKuPrm1hUDvW6KYKPJ7U"
          />
          
          <TextField
            fullWidth
            label="Secret Key"
            type={showSecretKey ? 'text' : 'password'}
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            error={!!errors.secretKey}
            helperText={errors.secretKey || 'Your secret key from the admin dashboard'}
            margin="normal"
            variant="outlined"
            placeholder="Enter your secret key"
            InputProps={{
              endAdornment: (
                <IconButton
                  onClick={() => setShowSecretKey(!showSecretKey)}
                  edge="end"
                  size="small"
                >
                  {showSecretKey ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              )
            }}
          />
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Security Note:</strong> Your credentials will be embedded in the HTML file. 
              Keep this file secure and never expose it publicly.
            </Typography>
          </Alert>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDownloadWithCredentials}
            variant="contained"
            startIcon={<Download />}
            sx={{
              backgroundColor: '#1976d2',
              '&:hover': { backgroundColor: '#1565c0' }
            }}
          >
            Download with Credentials
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DownloadSourceFilesSection;
